/**
 * FoodReportButton — small flag icon that lets a user flag a food record
 * (Nutrami or USDA) as having wrong macros / name / portion / duplicate.
 *
 * Post to  POST /api/foods/report  with {food_id, layer, reason, comment}.
 * Admin sees the queue at /api/admin/food-reports and inside the Admin panel.
 *
 * Placed next to `SourceBadge` inside FoodSearchOverlay food cards.
 */
import React, { useState } from "react";
import { Flag, X, AlertCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";

const REASONS = [
  { key: "wrong_calories", label_bg: "Грешни калории", label_en: "Wrong calories" },
  { key: "wrong_macros",   label_bg: "Грешни макроси",  label_en: "Wrong macros" },
  { key: "wrong_name",     label_bg: "Грешно име",       label_en: "Wrong name" },
  { key: "wrong_portion",  label_bg: "Грешна порция",    label_en: "Wrong portion" },
  { key: "duplicate",      label_bg: "Дубликат",         label_en: "Duplicate" },
  { key: "other",          label_bg: "Друго",            label_en: "Other" },
];

export default function FoodReportButton({ foodId, foodLayer, foodName, bg }) {
  const { API } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("wrong_calories");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Only report supported layers (nutrami / usda). Skip for supplements etc.
  if (!foodId || !foodLayer) return null;
  if (foodLayer !== "nutrami" && foodLayer !== "usda") return null;

  const submit = async (e) => {
    e?.stopPropagation();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/foods/report`, {
        food_id: foodId,
        layer: foodLayer,
        reason,
        comment: comment.trim() || null,
      });
      toast.success(bg ? "Благодарим! Докладът е изпратен." : "Thanks! Report submitted.");
      setOpen(false);
      setComment("");
      setReason("wrong_calories");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка при изпращане" : "Send failed"));
    }
    setSubmitting(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="inline-flex items-center justify-center h-4 w-4 rounded hover:bg-red-500/15 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
        title={bg ? "Съобщи за грешка" : "Report bad data"}
        data-testid={`food-report-btn-${foodId}`}
      >
        <Flag className="w-2.5 h-2.5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="food-report-modal"
        >
          <div
            className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-lg shadow-xl p-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <h3 className="font-bold text-sm">
                  {bg ? "Съобщи за грешка" : "Report bad data"}
                </h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-2 bg-gray-50 dark:bg-gray-800 rounded text-[11px]">
              <span className="text-gray-500">{bg ? "Храна:" : "Food:"}</span>{" "}
              <span className="font-semibold">{foodName || foodId}</span>
              <span className="ml-2 text-gray-400">
                ({foodLayer === "nutrami" ? "Nutrami" : "USDA"})
              </span>
            </div>

            <div>
              <label className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                {bg ? "Причина" : "Reason"}
              </label>
              <div className="mt-1 grid grid-cols-2 gap-1">
                {REASONS.map((r) => (
                  <button
                    key={r.key} type="button"
                    onClick={() => setReason(r.key)}
                    data-testid={`report-reason-${r.key}`}
                    className={`px-2 py-1.5 rounded text-[10px] font-medium border transition-colors ${
                      reason === r.key
                        ? "bg-red-500 text-white border-red-500"
                        : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:border-red-400"
                    }`}
                  >
                    {bg ? r.label_bg : r.label_en}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] font-semibold text-gray-600 dark:text-gray-400">
                {bg ? "Коментар (по избор)" : "Comment (optional)"}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full mt-1 text-xs p-2 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800"
                placeholder={bg ? "Напр. калориите изглеждат твърде високи" : "e.g. calories look too high"}
                data-testid="report-comment"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setOpen(false)}
                className="flex-1 h-8 rounded border text-xs font-semibold border-gray-300 dark:border-gray-700"
                data-testid="report-cancel"
              >
                {bg ? "Отказ" : "Cancel"}
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="flex-1 h-8 rounded text-xs font-semibold bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-1"
                data-testid="report-submit"
              >
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
                {bg ? "Изпрати" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
